/* Maison Theme JS — Cart AJAX · Mobile Nav · Header · Gallery · Filters */
'use strict';

/* ---------------------------------------------------------------
   Utilities
--------------------------------------------------------------- */
const qs = (sel, ctx = document) => ctx.querySelector(sel);
const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function debounce(fn, ms = 200) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
}

/* ---------------------------------------------------------------
   AJAX Cart
--------------------------------------------------------------- */
const Cart = {
  _overlay: null,
  _drawer: null,
  _count: null,

  init() {
    this._overlay = qs('#cart-overlay');
    this._drawer  = qs('#cart-drawer');
    this._count   = qs('#cart-count');

    // Toggle drawer open/close
    qsa('.header-cart-toggle').forEach(btn => {
      btn.addEventListener('click', () => this.open());
    });

    qs('.cart-drawer-close')?.addEventListener('click', () => this.close());
    this._overlay?.addEventListener('click', () => this.close());

    // Escape key
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });

    // Quantity changes & remove in drawer
    this._drawer?.addEventListener('click', e => {
      const qtyBtn = e.target.closest('.qty-btn[data-key]');
      const removeBtn = e.target.closest('.cart-item-remove[data-key]');

      if (qtyBtn) {
        e.preventDefault();
        const key = qtyBtn.dataset.key;
        const valueEl = qtyBtn.closest('.qty-control').querySelector('.qty-value');
        let qty = parseInt(valueEl?.textContent || '1');
        qty = qtyBtn.dataset.action === 'increase' ? qty + 1 : Math.max(0, qty - 1);
        this.updateItem(key, qty);
      }

      if (removeBtn) {
        e.preventDefault();
        this.updateItem(removeBtn.dataset.key, 0);
      }
    });

    // Product page quantity buttons
    document.addEventListener('click', e => {
      const btn = e.target.closest('.qty-control--large .qty-btn');
      if (!btn) return;
      const input = btn.closest('.qty-control').querySelector('.qty-input');
      if (!input) return;
      let v = parseInt(input.value) || 1;
      input.value = btn.dataset.action === 'increase' ? v + 1 : Math.max(1, v - 1);
    });

    // Cart page quantity buttons — update automatically (AJAX)
    document.addEventListener('click', e => {
      const btn = e.target.closest('.cart-table .qty-btn');
      if (!btn) return;
      const control = btn.closest('.qty-control');
      const input = control?.querySelector('.qty-input');
      if (!input) return;
      const v = parseInt(input.value) || 0;
      const next = btn.dataset.action === 'increase' ? v + 1 : Math.max(0, v - 1);
      input.value = next;
      const section = btn.closest('[data-section-id]');
      this.updateCartPage(parseInt(control.dataset.line), next, section?.dataset.sectionId);
    });

    // Cart page: typing a quantity directly (debounced)
    const onQtyInput = debounce(input => {
      const control = input.closest('.qty-control');
      const section = input.closest('[data-section-id]');
      const qty = Math.max(0, parseInt(input.value) || 0);
      this.updateCartPage(parseInt(control.dataset.line), qty, section?.dataset.sectionId);
    }, 500);
    document.addEventListener('change', e => {
      const input = e.target.closest('.cart-table .qty-input');
      if (input) onQtyInput(input);
    });

    // Add to cart — intercept any form posting to /cart/add (AJAX, no redirect)
    document.addEventListener('submit', e => {
      const form = e.target.closest('form');
      if (!form) return;
      const action = (form.getAttribute('action') || '').toLowerCase();
      const addBtn = qs('[name="add"]', form) || qs('.product-add-btn', form);
      const formId = form.getAttribute('id') || '';
      const isCartAdd = action.indexOf('/cart/add') !== -1 || formId.startsWith('product-form-') || !!addBtn;
      if (!isCartAdd || !addBtn) return;
      e.preventDefault();

      // Variant pickers live outside the <form> element (associated via form="id").
      // Sync the hidden #id input from the live checked radio state right before
      // FormData is captured — uses getAttribute('id') to avoid HTMLFormElement's
      // named property access returning the <input name="id"> element instead of
      // the form's own id string.
      const pdEl = qs('[id^="product-json-"]');
      const idInput = qs('[name="id"]', form);
      if (pdEl && idInput && formId) {
        try {
          const pd = JSON.parse(pdEl.textContent);
          const inputs = [...new Set([
            ...qsa('[name^="option"]', form),
            ...qsa(`[name^="option"][form="${formId}"]`, document)
          ])];
          const selected = inputs
            .map(inp => inp.type === 'radio' ? (inp.checked ? inp.value : null) : inp.value)
            .filter(Boolean);
          const v = pd.variants.find(vt => vt.options.every((o, i) => o === selected[i]));
          if (v) idInput.value = v.id;
        } catch (_) {}
      }

      this.addToCart(new FormData(form), addBtn);
    }, true);

    // Quick add buttons on product cards
    document.addEventListener('click', e => {
      const btn = e.target.closest('.product-card-quick-add[data-product-id]');
      if (!btn || !btn.dataset.variantId) return;
      e.preventDefault();
      const fd = new FormData();
      fd.append('id', btn.dataset.variantId);
      fd.append('quantity', '1');
      this.addToCart(fd, btn);
    });
  },

  _getProductFormId() {
    const form = qs('[id^="product-form-"]');
    return form ? form.id.replace('product-form-', '') : '';
  },

  open() {
    this._drawer?.classList.add('is-open');
    this._overlay?.classList.add('is-visible');
    this._drawer?.removeAttribute('aria-hidden');
    qs('.header-cart-toggle')?.setAttribute('aria-expanded', 'true');
    qs('.cart-drawer-close')?.focus();
    document.body.style.overflow = 'hidden';
  },

  close() {
    this._drawer?.classList.remove('is-open');
    this._overlay?.classList.remove('is-visible');
    this._drawer?.setAttribute('aria-hidden', 'true');
    qs('.header-cart-toggle')?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  },

  async addToCart(formData, btn) {
    const originalText = btn.textContent.trim();
    btn.disabled = true;
    btn.textContent = '...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'Error adding to cart');
      }

      await this.refreshDrawer();

      if (window.matchMedia('(min-width: 768px)').matches) {
        this.open();
      } else {
        this._toast();
      }
    } catch (err) {
      console.error('[Cart]', err);
      btn.textContent = err.message;
      setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 2500);
      return;
    }

    btn.textContent = originalText;
    btn.disabled = false;
  },

  _toast() {
    const strings = window.cartStrings || {};
    const message = strings.added || 'Item added to cart';
    const cartUrl = strings.cartUrl || '/cart';
    const viewLabel = strings.viewCart || 'View cart';

    let toast = qs('#cart-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'cart-toast';
      toast.className = 'cart-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }

    toast.innerHTML =
      '<svg class="cart-toast-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>' +
      '<span class="cart-toast-text"></span>' +
      '<a class="cart-toast-link" href="' + cartUrl + '"></a>';
    qs('.cart-toast-text', toast).textContent = message;
    qs('.cart-toast-link', toast).textContent = viewLabel;

    clearTimeout(this._toastTimer);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    this._toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 3200);
  },

  async updateItem(key, quantity) {
    const itemsEl = qs('#cart-drawer-items');
    if (itemsEl) itemsEl.classList.add('cart-loading');

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      await this.refreshDrawer();
    } catch (err) {
      console.error('[Cart update]', err);
    }

    if (itemsEl) itemsEl.classList.remove('cart-loading');
  },

  async updateCartPage(line, quantity, sectionId) {
    if (!line) return;
    const sectionEl = sectionId ? document.getElementById('shopify-section-' + sectionId) : null;
    sectionEl?.classList.add('cart-loading');

    try {
      const body = { line, quantity };
      if (sectionId) {
        body.sections = sectionId;
        body.sections_url = window.location.pathname;
      }
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });
      const cart = await res.json();
      this._updateCount(cart.item_count);
      await this.refreshDrawer();

      if (sectionEl && cart.sections && cart.sections[sectionId] != null) {
        const doc = new DOMParser().parseFromString(cart.sections[sectionId], 'text/html');
        const fresh = doc.getElementById('shopify-section-' + sectionId);
        sectionEl.innerHTML = fresh ? fresh.innerHTML : doc.body.innerHTML;
      } else if (cart.item_count === 0) {
        window.location.reload();
      }
    } catch (err) {
      console.error('[Cart page update]', err);
    }

    sectionEl?.classList.remove('cart-loading');
  },

  async refreshDrawer() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this._updateCount(cart.item_count);
      this._renderItems(cart);
      this._updateSubtotal(cart.total_price, cart.currency);
    } catch (err) {
      console.error('[Cart refresh]', err);
    }
  },

  _renderItems(cart) {
    const itemsEl = qs('#cart-drawer-items');
    const footerEl = qs('#cart-drawer-footer');
    if (!itemsEl) return;

    if (cart.item_count === 0) {
      itemsEl.innerHTML = `
        <div class="cart-drawer-empty">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true">
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <path d="M16 10a4 4 0 0 1-8 0"/>
          </svg>
          <p>Your cart is empty</p>
          <a href="/collections" class="btn btn--primary">Continue Shopping</a>
        </div>`;
      if (footerEl) footerEl.hidden = true;
      return;
    }

    if (footerEl) footerEl.hidden = false;

    const items = cart.items.map(item => {
      const imgHtml = item.image
        ? `<img src="${item.image}" alt="${this._esc(item.title)}" width="70" height="70" loading="lazy">`
        : '';
      const variantHtml = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="cart-item-variant">${this._esc(item.variant_title)}</p>`
        : '';
      return `
        <li class="cart-item" data-key="${item.key}">
          <a href="${item.url}" class="cart-item-image-link">${imgHtml}</a>
          <div class="cart-item-info">
            <a href="${item.url}" class="cart-item-title">${this._esc(item.product_title)}</a>
            ${variantHtml}
            <div class="cart-item-meta">
              <div class="qty-control">
                <button class="qty-btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease quantity">−</button>
                <span class="qty-value" aria-live="polite">${item.quantity}</span>
                <button class="qty-btn" data-action="increase" data-key="${item.key}" aria-label="Increase quantity">+</button>
              </div>
              <span class="cart-item-price">${this._money(item.final_line_price, cart.currency)}</span>
            </div>
          </div>
          <button class="btn-icon cart-item-remove" data-key="${item.key}" aria-label="Remove ${this._esc(item.title)}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
              <polyline points="3,6 5,6 21,6"/>
              <path d="M19,6l-1,14a2,2,0,0,1-2,2H8a2,2,0,0,1-2-2L5,6"/>
              <path d="M10,11v6"/><path d="M14,11v6"/>
              <path d="M9,6V4a1,1,0,0,1,1-1h4a1,1,0,0,1,1,1V6"/>
            </svg>
          </button>
        </li>`;
    }).join('');

    itemsEl.innerHTML = `<ul class="cart-items-list" role="list">${items}</ul>`;
  },

  _updateCount(count) {
    if (!this._count) return;
    this._count.textContent = count;
    this._count.classList.toggle('cart-count--hidden', count === 0);
    qsa('.header-cart-toggle').forEach(btn => {
      btn.setAttribute('aria-label', `Cart (${count} items)`);
    });
  },

  _updateSubtotal(priceInCents, currency) {
    const el = qs('#cart-subtotal-price');
    if (el) el.textContent = this._money(priceInCents, currency);
  },

  _money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || window.Shopify?.currency?.active || 'USD'
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  },

  _esc(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
};

/* ---------------------------------------------------------------
   Mobile Navigation
--------------------------------------------------------------- */
const MobileNav = {
  _nav: null,
  _overlay: null,
  _toggle: null,

  init() {
    this._nav     = qs('#mobile-nav');
    this._overlay = qs('#mobile-nav-overlay');
    this._toggle  = qs('.header-menu-toggle');

    this._toggle?.addEventListener('click', () => this.open());
    qs('.mobile-nav-close')?.addEventListener('click', () => this.close());
    this._overlay?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });

    // Submenu toggles
    qsa('.mobile-nav-link--parent').forEach(btn => {
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', !expanded);
        const submenu = btn.nextElementSibling;
        if (submenu) {
          submenu.hidden = expanded;
          // Animate chevron
          const chevron = btn.querySelector('.nav-chevron');
          if (chevron) chevron.style.transform = expanded ? '' : 'rotate(180deg)';
        }
      });
    });
  },

  open() {
    this._nav?.classList.add('is-open');
    this._nav?.removeAttribute('aria-hidden');
    this._overlay?.classList.add('is-visible');
    this._toggle?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  },

  close() {
    this._nav?.classList.remove('is-open');
    this._nav?.setAttribute('aria-hidden', 'true');
    this._overlay?.classList.remove('is-visible');
    this._toggle?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }
};

/* ---------------------------------------------------------------
   Sticky Header scroll shadow
--------------------------------------------------------------- */
const Header = {
  init() {
    const header = qs('#site-header');
    if (!header?.classList.contains('header--sticky')) return;

    const onScroll = debounce(() => {
      header.classList.toggle('header--scrolled', window.scrollY > 20);
    }, 50);

    window.addEventListener('scroll', onScroll, { passive: true });
  }
};

/* ---------------------------------------------------------------
   Product Gallery
--------------------------------------------------------------- */
const Gallery = {
  init() {
    const gallery = document.getElementById('product-gallery');
    if (!gallery) return;
    const thumbs = gallery.querySelectorAll('.product-gallery-thumb');
    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => Gallery.goTo(i));
    });
  },

  goTo(index) {
    const gallery = document.getElementById('product-gallery');
    if (!gallery) return;
    const slides = gallery.querySelectorAll('.product-gallery-slide');
    const thumbs = gallery.querySelectorAll('.product-gallery-thumb');
    slides.forEach((s, i) => s.classList.toggle('active', i === index));
    thumbs.forEach((t, i) => t.classList.toggle('active', i === index));
  },

  goToImageId(imageId) {
    if (!imageId) return false;
    const gallery = document.getElementById('product-gallery');
    if (!gallery) return false;
    const slides = [...gallery.querySelectorAll('.product-gallery-slide')];
    const index = slides.findIndex(s => s.id === `gallery-slide-${imageId}`);
    if (index >= 0) { Gallery.goTo(index); return true; }
    return false;
  },

  goToImageByAlt(needle) {
    if (!needle) return false;
    const gallery = document.getElementById('product-gallery');
    if (!gallery) return false;
    const slides = [...gallery.querySelectorAll('.product-gallery-slide')];
    const lower = String(needle).toLowerCase().trim();
    const index = slides.findIndex(s => {
      const alt = (s.querySelector('img')?.getAttribute('alt') || '').toLowerCase();
      return alt.includes(lower);
    });
    if (index >= 0) { Gallery.goTo(index); return true; }
    return false;
  }
};

/* ---------------------------------------------------------------
   Product Variants — update price + button state
--------------------------------------------------------------- */
const ProductVariants = {
  init() {
    const productDataEl = qs('[id^="product-json-"]');
    if (!productDataEl) return;

    let product;
    try { product = JSON.parse(productDataEl.textContent); } catch { return; }

    const form = qs('[id^="product-form-"]');
    if (!form) return;

    const priceEl   = qs('#product-price', form.closest('.product-info') || document);
    const compareEl = qs('#product-compare-price', form.closest('.product-info') || document);
    const addBtn    = qs('#product-add-btn');
    const variantInput = qs('#product-variant-id');

    const onOptionChange = () => {
      const selected = this._getSelectedOptions(form);
      const variant = product.variants.find(v =>
        v.options.every((opt, i) => opt === selected[i])
      );
      if (!variant) return;

      if (variantInput) variantInput.value = variant.id;

      if (priceEl) {
        priceEl.textContent = this._formatMoney(variant.price);
        priceEl.classList.toggle('price--sale', variant.compare_at_price > variant.price);
      }

      if (compareEl) {
        if (variant.compare_at_price > variant.price) {
          compareEl.textContent = this._formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }

      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available
          ? (addBtn.dataset.addText || 'Add to Cart')
          : (addBtn.dataset.soldOutText || 'Sold Out');
      }

      // Sync gallery to selected variant — try multiple strategies
      // 1. Variant's featured_image (best, when merchant assigned one)
      // 2. Match selected color value against image alt text (for POD products like Yoycol)
      let switched = false;
      if (variant.featured_image && variant.featured_image.id) {
        switched = Gallery.goToImageId(variant.featured_image.id);
      }
      if (!switched) {
        const colorIdx = product.options.findIndex(o => {
          const n = String(o).toLowerCase();
          return n === 'color' || n === 'colour' || n === 'צבע' || n === 'اللون';
        });
        if (colorIdx >= 0) {
          const colorValue = variant.options[colorIdx];
          if (colorValue) Gallery.goToImageByAlt(colorValue);
        }
      }

      // Update URL without reload
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    // Listen at document level: pickers live outside the <form> element (associated
    // via form="<id>"), so change events never bubble into the form itself.
    const formId = form.getAttribute('id') || '';
    document.addEventListener('change', e => {
      const el = e.target;
      if (!el.name || !el.name.startsWith('option')) return;
      const associatedForm = el.form || document.getElementById(el.getAttribute('form'));
      if (associatedForm !== form) return;
      onOptionChange();
    });
  },

  _getSelectedOptions(form) {
    const fId = form.getAttribute('id') || '';
    const inside = [...qsa('[name^="option"]', form)];
    const outside = fId ? [...qsa(`[name^="option"][form="${fId}"]`, document)] : [];
    const inputs = [...new Set([...inside, ...outside])];
    return inputs.map(input => {
      if (input.type === 'radio') return input.checked ? input.value : null;
      return input.value;
    }).filter(Boolean);
  },

  _formatMoney(cents) {
    return (cents / 100).toLocaleString(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
      minimumFractionDigits: 2
    });
  }
};

/* ---------------------------------------------------------------
   Collection — Filters, Sort, View Toggle
--------------------------------------------------------------- */
const Filters = {
  init() {
    const toggle  = qs('#coll-filter-toggle');
    const sidebar = qs('#coll-sidebar');
    const overlay = qs('#coll-sidebar-overlay');
    const closeBtn = qs('.coll-sidebar-close');

    if (sidebar) {
      toggle?.addEventListener('click', () => {
        const open = sidebar.classList.contains('is-open');
        open ? this._close(toggle, sidebar, overlay) : this._open(toggle, sidebar, overlay);
      });
      closeBtn?.addEventListener('click', () => this._close(toggle, sidebar, overlay));
      overlay?.addEventListener('click', () => this._close(toggle, sidebar, overlay));
    }

    // Sort — redirect on change
    qs('#coll-sort-select')?.addEventListener('change', e => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', e.target.value);
      window.location.href = url.toString();
    });

    // Filter form — apply automatically on change (no Apply button needed)
    const filterForm = qs('#filter-form');
    if (filterForm) {
      const apply = form => {
        const url = new URL(window.location.href);
        [...url.searchParams.keys()].filter(k => k.startsWith('filter.')).forEach(k => url.searchParams.delete(k));
        new FormData(form).forEach((val, key) => {
          if (val) url.searchParams.append(key, val);
        });
        window.location.href = url.toString();
      };

      // Checkboxes / radios apply instantly; number (price) inputs apply on commit.
      filterForm.addEventListener('change', e => {
        const t = e.target;
        if (t.type === 'number') return;
        apply(filterForm);
      });
      qsa('#filter-form input[type="number"]').forEach(input => {
        input.addEventListener('change', () => apply(filterForm));
        input.addEventListener('keydown', e => {
          if (e.key === 'Enter') { e.preventDefault(); apply(filterForm); }
        });
      });

      // JS active — hide the manual Apply button (kept as a no-JS fallback)
      filterForm.classList.add('filter-form--auto');

      // Submit still works (Enter on a field, no-JS) as a safety net.
      filterForm.addEventListener('submit', e => {
        e.preventDefault();
        apply(filterForm);
      });
    }

    // View toggle (grid columns)
    qsa('.coll-view-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const cols = btn.dataset.cols;
        const grid = qs('#product-list');
        if (!grid) return;

        qsa('.coll-view-btn').forEach(b => {
          b.classList.remove('active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');

        grid.className = `product-grid product-grid--${cols}-col`;
      });
    });
  },

  _open(toggle, sidebar, overlay) {
    toggle?.setAttribute('aria-expanded', 'true');
    sidebar.classList.add('is-open');
    overlay?.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
  },

  _close(toggle, sidebar, overlay) {
    toggle?.setAttribute('aria-expanded', 'false');
    sidebar.classList.remove('is-open');
    overlay?.classList.remove('is-visible');
    document.body.style.overflow = '';
  }
};

/* ---------------------------------------------------------------
   Lookbook hotspot focus
--------------------------------------------------------------- */
const Lookbook = {
  init() {
    qsa('.lookbook-hotspot').forEach(btn => {
      btn.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const popup = btn.nextElementSibling;
          if (popup) {
            const visible = popup.style.opacity === '1';
            popup.style.opacity = visible ? '0' : '1';
            popup.style.visibility = visible ? 'hidden' : 'visible';
            popup.style.pointerEvents = visible ? 'none' : 'auto';
          }
        }
      });
    });
  }
};

/* ---------------------------------------------------------------
   Announcement bar auto-dismiss (optional)
--------------------------------------------------------------- */
const AnnouncementBar = {
  init() {
    // Nothing required — can add dismiss button here if needed
  }
};

/* ---------------------------------------------------------------
   Wishlist — visual toggle with localStorage persistence
--------------------------------------------------------------- */
const Wishlist = {
  _key: 'maison_wishlist',

  init() {
    this._restore();

    document.addEventListener('click', e => {
      const btn = e.target.closest('.product-card-wishlist[data-product-id]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      this._toggle(btn);
    });
  },

  _restore() {
    const saved = this._load();
    document.querySelectorAll('.product-card-wishlist[data-product-id]').forEach(btn => {
      if (saved.includes(btn.dataset.productId)) this._setActive(btn, true);
    });
  },

  _toggle(btn) {
    const active = btn.classList.contains('is-wishlisted');
    this._setActive(btn, !active);
    const saved = this._load();
    const pid = btn.dataset.productId;
    const updated = active ? saved.filter(id => id !== pid) : [...new Set([...saved, pid])];
    try { localStorage.setItem(this._key, JSON.stringify(updated)); } catch (_) {}
  },

  _setActive(btn, active) {
    btn.classList.toggle('is-wishlisted', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  },

  _load() {
    try { return JSON.parse(localStorage.getItem(this._key) || '[]'); } catch (_) { return []; }
  }
};

/* ---------------------------------------------------------------
   Init all on DOM ready
--------------------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  Cart.init();
  MobileNav.init();
  Header.init();
  Gallery.init();
  ProductVariants.init();
  Filters.init();
  Lookbook.init();
  Wishlist.init();
});

/* ---------------------------------------------------------------
   Scroll reveal — page sections fade in sequentially (staggered)
--------------------------------------------------------------- */
(function initSectionReveal() {
  const root = document.getElementById('main-content');
  if (!root) return;

  const sections = qsa(':scope > .shopify-section', root);
  if (!sections.length) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // No-motion users, or browsers without IntersectionObserver: reveal everything now.
  if (reduceMotion || !('IntersectionObserver' in window)) {
    sections.forEach((s) => s.classList.add('is-revealed'));
    return;
  }

  const STAGGER_MS = 110;
  const unrevealed = new Set(sections);

  const revealNow = (el, delay) => {
    el.style.transitionDelay = delay + 'ms';
    el.classList.add('is-revealed');
    unrevealed.delete(el);
  };

  const fallback = setTimeout(() => {
    [...unrevealed].forEach((s) => revealNow(s, 0));
  }, 600);

  const observer = new IntersectionObserver((entries, obs) => {
    entries
      .filter((entry) => entry.isIntersecting)
      .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
      .forEach((entry, i) => {
        revealNow(entry.target, i * STAGGER_MS);
        obs.unobserve(entry.target);
      });
    if (!unrevealed.size) clearTimeout(fallback);
  }, { threshold: 0.05, rootMargin: '0px 0px -40px 0px' });

  sections.forEach((s) => observer.observe(s));
})();
